///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { AxesHelper, Box3, Vector3 } from "three";
import { IComponent, components, Viewer } from "@inweb/viewer-three";

class AxesHelperComponent implements IComponent {
  private viewer: Viewer;
  private axesHelper1: AxesHelper;
  private axesHelper2: AxesHelper;
  private modelHelpers: AxesHelper[];

  constructor(viewer: Viewer) {
    this.axesHelper1 = new AxesHelper(1);
    this.axesHelper2 = new AxesHelper(1);
    this.modelHelpers = [];

    this.axesHelper1.setColors("#ccc", "#ccc", "#ccc");

    this.viewer = viewer;
    this.viewer.addEventListener("initialize", this.syncHelper);
    this.viewer.addEventListener("geometryend", this.syncHelper);
    this.viewer.addEventListener("clear", this.syncHelper);
  }

  dispose() {
    this.modelHelpers.forEach((helper) => {
      helper.removeFromParent();
      helper.dispose();
    });

    this.axesHelper1.removeFromParent();
    this.axesHelper1.dispose();

    this.axesHelper2.removeFromParent();
    this.axesHelper2.dispose();

    this.viewer.removeEventListener("initialize", this.syncHelper);
    this.viewer.removeEventListener("geometryend", this.syncHelper);
    this.viewer.removeEventListener("clear", this.syncHelper);
  }

  syncHelper = () => {
    this.modelHelpers.forEach((helper) => {
      helper.removeFromParent();
      helper.dispose();
    });
    this.modelHelpers.length = 0;

    this.axesHelper1.removeFromParent();
    this.axesHelper2.removeFromParent();

    const size = this.viewer.extents.getSize(new Vector3()).length();
    const center = this.viewer.extents.getCenter(new Vector3());

    this.axesHelper1.position.set(0, 0, 0);
    this.axesHelper1.scale.setScalar(size * 1.25);

    this.axesHelper2.position.copy(center);
    this.axesHelper2.scale.setScalar(size);

    this.viewer.helpers.add(this.axesHelper1);
    this.viewer.helpers.add(this.axesHelper2);

    if (this.viewer.models.length < 2) return;

    this.viewer.models.forEach((model) => {
      const extents = model.getExtents(new Box3());
      const size = extents.getSize(new Vector3()).length();
      const center = extents.getCenter(new Vector3());

      const helper = new AxesHelper(size);
      helper.position.copy(center);

      this.modelHelpers.push(helper);
      this.viewer.helpers.add(helper);
    });
  };
}

components.registerComponent("AxesHelperComponent", (viewer) => new AxesHelperComponent(viewer));
